import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Breadcrumbs, classnames, LoadingIndicator, Select, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { ValidatingInput, ValidationForm, ValidationSummary } from '@/components/forms/validation';
import ProfileIcon from '@/components/Icons/EntityIcon/ProfileIcon';
import { HttpStatusCode } from '@/HttpStatusCodes';
import { uniq } from 'lodash-es';
import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { addDependency, DependencyConflictCode, DependencyItemDto, getDependency } from '@/services/dependencies';
import { LinkingType, OptionGroupDto, OptionItemDto } from '@/types';
import { Point } from '@/utils/graphics';
import { useAsyncState, useGetProjectId } from '@/utils/hooks';
import { legalDependency } from '../Dependencies.util';
import { BreadcrumbsProjectLink } from '@/components/navigation';
import theme from './DependenciesEdit.module.scss';
import { DependencyBox } from './DependencyBox';
import { ProjectPermissionToggle } from '@/components/permissions';
import { AlertBanner, Button, ButtonBar, SlideToggle } from '@ygtechnologies/rubus-ui';
import { FilterOptionOption } from 'react-select';
import { searchOptionGroups } from '@/services/options/option-groups';
import { getOptionItemsByGroup } from '@/services/options/option-items';
import TagsDisplay from '@/components/TagsDisplay';
import OptionGroupLabel from '@/seller/components/options/OptionGroupLabel/OptionGroupLabel';

interface GroupDropdownItem {
    group: OptionGroupDto;
}

const config = {
    boxHeight: 100,
    boxSpacing: 30,
    boxWidth: 500,
    spacing: 200,
    checkboxSize: 30,
};

/// Also used in BuyerOptionPresetMatrix
export const OptionGroupDropdownItem = ({ group }: GroupDropdownItem) => {
    return (
        <div className={theme.dropdownItem}>
            <div className="flex">
                <ProfileIcon sizeInPx={35} profileId={group.providedByProfile?.id ?? null} className={theme.profileIcon} />
                <div>
                    <div>
                        <div className={theme.name}>
                            <OptionGroupLabel name={group.name} internalNote={group.internalNote} />
                        </div>
                        <div className={theme.description}>{group.description}</div>
                    </div>
                </div>
            </div>
            <TagsDisplay
                size="small"
                badgeSize="xs"
                tagIds={[
                    ...(group.linkType === LinkingType.Room ? group.linkedRooms.map((x) => x.roomId) : []),
                    ...group.tags.map((x) => x.id),
                ]}
            />
        </div>
    );
};

interface DependenciesEditProps {
    dependencyLinkId?: number;
}

interface DependencyLink {
    id?: number;
    fromOptionGroupId: number | null;
    toOptionGroupId: number | null;
}

interface Option {
    label: ReactNode;
    value: number;
    isDisabled: boolean;
}

// Maps the backend's 409 conflict code to a specific message. Falls back to the generic
// "already exists" text for an unknown/absent code (e.g. an older backend).
const getConflictMessage = (code?: string): string => {
    switch (code) {
        case DependencyConflictCode.Circular:
            return t`This link would create a circular dependency between the groups`;
        case DependencyConflictCode.DeadlineMismatch:
            return t`Both groups must have the same deadline`;
        case DependencyConflictCode.LinkTypeMismatch:
            return t`Both groups must be of the same type (both room-linked or both direct)`;
        case DependencyConflictCode.AlreadyExists:
        default:
            return t`A dependency between the selected groups already exists`;
    }
};

const DependenciesEdit = ({ dependencyLinkId }: DependenciesEditProps) => {
    const projectId = useGetProjectId();
    const [name, setName] = useState<string>('');
    const [optionGroups] = useAsyncState(() => searchOptionGroups({ projectId: projectId! }), [projectId], []);
    const [showAllLines, setShowAllLines] = useState(true);
    const [isSimpleMode, setSimpleMode] = useState(true);
    const [link, setLink] = useState<DependencyLink | null>(null);
    const [activeOptionId, setActiveOptionId] = useState<number | null>(null);
    const [dependencyItems, setDependencyItems] = useState<DependencyItemDto[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [reverseLines, setReverseLines] = useState(false);
    const [hoverItemId, setHoverItemId] = useState<number | null>(null);
    const history = useHistory();
    const selectedGroups = optionGroups.filter((x) => x.id === link?.fromOptionGroupId || x.id === link?.toOptionGroupId);

    const invalidDeadline = uniq(selectedGroups.map((x) => x.deadlineId)).length > 1;

    // Dependencies across mismatched link types (room-linked vs direct) never fire in the option engine,
    // so the backend rejects them too. Block it here before save.
    const invalidLinkType = uniq(selectedGroups.map((x) => x.linkType)).length > 1;

    const invalidSelection = invalidDeadline || invalidLinkType;

    const fromSelectOptions = useMemo(() => {
        return optionGroups.map<Option>((x) => ({
            label: <OptionGroupDropdownItem group={x} />,
            value: x.id,
            isDisabled: x.id === link?.toOptionGroupId,
        }));
    }, [optionGroups, link]);

    const toSelectOptions = useMemo(() => {
        return optionGroups.map<Option>((x) => ({
            label: <OptionGroupDropdownItem group={x} />,
            value: x.id,
            isDisabled: x.id === link?.fromOptionGroupId,
        }));
    }, [optionGroups, link]);

    const [fromOptions] = useAsyncState(
        () => (link?.fromOptionGroupId ? getOptionItemsByGroup(link.fromOptionGroupId) : Promise.resolve(null)),
        [link?.fromOptionGroupId],
        null
    );
    const [toOptions] = useAsyncState(
        () => (link?.toOptionGroupId ? getOptionItemsByGroup(link.toOptionGroupId) : Promise.resolve(null)),
        [link?.toOptionGroupId],
        null
    );

    useEffect(() => {
        const loadData = async () => {
            const dep = await getDependency(dependencyLinkId!);
            setLink({
                id: dep.id,
                fromOptionGroupId: dep.fromOptionGroupId,
                toOptionGroupId: dep.toOptionGroupId,
            });
            setDependencyItems(dep.dependencyItems);
            setName(dep.name);
        };

        if (dependencyLinkId) {
            loadData();
        } else {
            setLink({
                fromOptionGroupId: null,
                toOptionGroupId: null,
            });
            setDependencyItems([]);
            setName('');
        }
    }, [dependencyLinkId]);

    const updateDependency = (dep: DependencyItemDto) => {
        const findExisting = (x: DependencyItemDto) =>
            (dep.from === x.from || dep.from === x.to) && (dep.to === x.to || dep.to === x.from);

        const existingDep = dependencyItems.find(findExisting);

        if (existingDep) {
            setDependencyItems(dependencyItems.filter((x) => !findExisting(x)));
        } else {
            setDependencyItems([...dependencyItems, dep]);
        }
    };

    const handleOptionSelect = (linkGroupItemId: number) => {
        setActiveOptionId((current) => {
            if (current === linkGroupItemId) {
                return null;
            }

            return linkGroupItemId;
        });
    };

    const renderLines = () => {
        if (!fromOptions || !toOptions) {
            return null;
        }
        const lines: ReactElement[] = [];
        const highlightedLines: ReactElement[] = [];
        const hoverLines: ReactElement[] = [];

        fromOptions.forEach((from, fromIndex) => {
            toOptions.forEach((to, toIndex) => {
                if (legalDependency(from.id, to.id, dependencyItems) !== reverseLines) {
                    const highlight = from.id === activeOptionId || to.id === activeOptionId;
                    const hover = from.id === hoverItemId || to.id === hoverItemId;

                    const fromPoint = new Point(
                        config.boxWidth - config.checkboxSize / 2,
                        fromIndex * (config.boxHeight + config.boxSpacing) + config.boxHeight / 2
                    );

                    const toPoint = new Point(
                        config.boxWidth + config.spacing + config.checkboxSize / 2,
                        toIndex * (config.boxHeight + config.boxSpacing) + config.boxHeight / 2
                    );

                    if (showAllLines || highlight || hover) {
                        const line = (
                            <line
                                className={classnames(theme.line, {
                                    [theme.hover]: hover,
                                    [theme.highlight]: highlight,
                                    [theme.reverse]: reverseLines,
                                })}
                                key={`${from.id}_${to.id}`}
                                x1={fromPoint.x}
                                y1={fromPoint.y}
                                x2={toPoint.x}
                                y2={toPoint.y}
                            />
                        );

                        if (highlight) {
                            highlightedLines.push(line);
                        } else if (hover) {
                            hoverLines.push(line);
                        } else {
                            lines.push(line);
                        }
                    }
                }
            });
        });

        return [...lines, ...hoverLines, ...highlightedLines];
    };

    const renderOptionList = (options: OptionItemDto[] | null, side: 'left' | 'right') => {
        if (!options || options.length === 0) {
            return null;
        }

        const activeOption = fromOptions?.concat(toOptions || []).find((x) => x.id === activeOptionId);
        const numberOfCheckedOptions = options.reduce((acc, curr) => {
            const isChecked = legalDependency(activeOptionId || 0, curr.id, dependencyItems);
            if (isChecked) {
                return acc + 1;
            }
            return acc;
        }, 0);
        return options.map((option, i) => {
            const isChecked = legalDependency(activeOptionId || 0, option.id, dependencyItems);

            const isLastOption = isChecked && numberOfCheckedOptions === 1;
            // Allow if it's not checked to fix edge case where they've changed default option.
            const isDefaultToDefault = activeOption?.isDefault && option.isDefault && isChecked;
            const disabledErrorText = isLastOption
                ? t`At least one option must be selected`
                : isDefaultToDefault
                  ? t`Cannot unselect from default to default`
                  : '';
            return (
                <foreignObject
                    key={option.id}
                    x={side === 'left' ? 0 : config.boxWidth + config.spacing}
                    y={i * (config.boxHeight + config.boxSpacing)}
                    height={config.boxHeight}
                    width={config.boxWidth}>
                    <DependencyBox
                        onHover={() => setHoverItemId(option.id)}
                        onLeave={() => setHoverItemId(null)}
                        checked={isChecked}
                        onDependencyItemChange={updateDependency}
                        activeOptionId={activeOptionId}
                        option={option}
                        onSelect={isSimpleMode && side === 'right' ? undefined : () => handleOptionSelect(option.id)}
                        checkboxPosition={side === 'left' ? 'right' : 'left'}
                        hideCheckbox={!activeOptionId || !!options?.find((x) => x.id === activeOptionId)}
                        disabled={isLastOption || isDefaultToDefault}
                        onDisabledClick={() => Toast.error(disabledErrorText)}
                    />
                </foreignObject>
            );
        });
    };

    const handleSave = async () => {
        if (!link) {
            return;
        }

        setIsSaving(true);
        try {
            const result = await addDependency({
                id: dependencyLinkId,
                projectId: projectId!,
                fromOptionGroupId: link.fromOptionGroupId!,
                toOptionGroupId: link.toOptionGroupId!,
                dependencyItems: dependencyItems,
                name,
            });

            if (dependencyLinkId !== result.id) {
                history.replace(`edit/${result.id}`);
            }

            Toast.success(t`Dependency saved`);
        } catch (e: any) {
            if (e.status === HttpStatusCode.Conflict) {
                const body = await e.json().catch(() => null);
                Toast.error(getConflictMessage(body?.code));
            } else {
                throw e;
            }
        } finally {
            setIsSaving(false);
        }
    };
    const handleLinkChange = (changes: Partial<DependencyLink>) => {
        setActiveOptionId(null);
        setDependencyItems([]);

        setLink((oldValue) => ({ ...oldValue!, ...changes }));
    };

    const handleFilterOption = (option: FilterOptionOption<Option>, searchText: string) => {
        const optionGroup = optionGroups!.find((x) => x.id === +option.value)!;
        return optionGroup.name.toLowerCase().includes(searchText.toLowerCase());
    };

    if (!link) {
        return <LoadingIndicator />;
    }

    return (
        <ValidationForm onSubmit={handleSave}>
            <div className={theme.main} style={{ '--checkbox-size': config.checkboxSize + 'px' } as any}>
                <Breadcrumbs>
                    <BreadcrumbsProjectLink to="/options/dependencies">
                        <Trans>Dependencies</Trans>
                    </BreadcrumbsProjectLink>

                    <Breadcrumbs.Item>
                        <span>{name || t`Unnamed dependency`}</span>
                    </Breadcrumbs.Item>
                </Breadcrumbs>
                <div>
                    <ValidatingInput
                        type="text"
                        className="form-control"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t`Name`}
                        required
                    />
                </div>

                <div className={theme.viewOptions}>
                    <SlideToggle onChange={(e) => setShowAllLines(e.target.checked)} checked={showAllLines}>
                        <Trans comment="toggle in dependencies">Show all lines</Trans>
                    </SlideToggle>
                    <SlideToggle onChange={(e) => setSimpleMode(e.target.checked)} checked={isSimpleMode}>
                        <Trans comment="toggle in dependencies">Simple mode</Trans>
                    </SlideToggle>
                    {/* <SlideToggle onChange={(e) => setReverseLines(e.target.checked)} checked={reverseLines}>
                        Reverse lines
                    </SlideToggle> */}
                </div>

                <div className={theme.groups}>
                    <label>
                        {/* <span>From</span> */}
                        <Select<Option>
                            filterOption={handleFilterOption}
                            isDisabled={!!dependencyLinkId}
                            className={theme.groupDropdown}
                            onChange={(x: any) => handleLinkChange({ fromOptionGroupId: x.value })}
                            value={fromSelectOptions.find((x) => x.value === link.fromOptionGroupId)}
                            options={fromSelectOptions}
                        />
                    </label>
                    <label>
                        {/* <span>To</span> */}
                        <Select<Option>
                            filterOption={handleFilterOption}
                            isDisabled={!!dependencyLinkId}
                            className={theme.groupDropdown}
                            onChange={(x: any) => handleLinkChange({ toOptionGroupId: x.value })}
                            value={toSelectOptions.find((x) => x.value === link.toOptionGroupId)}
                            options={toSelectOptions}
                        />
                    </label>
                </div>

                {invalidDeadline && (
                    <AlertBanner type="danger">
                        <Trans>Both groups must have the same deadline</Trans>
                    </AlertBanner>
                )}

                {invalidLinkType && (
                    <AlertBanner type="danger">
                        <Trans>Both groups must be of the same type (both room-linked or both direct)</Trans>
                    </AlertBanner>
                )}

                {!invalidSelection && (
                    <svg
                        height={
                            Math.max(fromOptions?.length ?? 0, toOptions?.length ?? 0) *
                                (config.boxHeight + config.boxSpacing) +
                            'px'
                        }>
                        {renderLines()}
                        {renderOptionList(fromOptions, 'left')}
                        {renderOptionList(toOptions, 'right')}
                    </svg>
                )}

                <ValidationSummary />
                <ProjectPermissionToggle condition={'isAdministrator'}>
                    <ButtonBar>
                        <ButtonBar.Right>
                            <Button
                                disabled={!link.fromOptionGroupId || !link.toOptionGroupId || invalidSelection}
                                loading={isSaving}
                                type="submit"
                                variant="primary">
                                <Trans>Save</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ProjectPermissionToggle>
            </div>
        </ValidationForm>
    );
};

export default DependenciesEdit;
